import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../../../api/apiClient';
import DonutChart from '../../../organization/components/charts/DonutChart';
import BarChartCard from '../../../organization/components/charts/BarChartCard';
import GroupedBarChartCard from '../../../organization/components/charts/GroupedBarChartCard';
import LineTrendChart from '../../../organization/components/charts/LineTrendChart';
import { CHART_COLORS } from '../../../organization/components/charts/chartSetup';

const EMPTY_DASHBOARD = {
  counters: {
    total_learners: 0,
    total_instructors: 0,
    total_org_admins: 0,
    total_courses: 0,
    total_enrollments: 0,
    total_assessments: 0,
    total_organizations: 0
  },
  users: { by_segment: { learner: 0, instructor: 0, org_learner: 0, org_instructor: 0 } },
  courses: { all_by_enrollment: [] },
  organizations: { by_user_type: [] },
  assessments: { by_assessment: [] },
  vlt: { by_type: { video: 0, document: 0, pdf: 0 } },
  licenses: { by_organization: [] },
  vouchers: { chart: { created: 0, used: 0 } },
  payments: { total_received: 0, by_type: [], trend: [] },
  forums: { by_forum: [] },
  surveys: { by_survey: [] },
  support_tickets: { summary: { received: 0, pending: 0, resolved: 0, completed: 0 } }
};

const KPI_COUNTERS = [
  { key: 'total_learners', label: 'Total Learners', icon: 'fas fa-user-graduate', color: 'text-blue-600', bg: 'from-blue-50 to-blue-100', path: '/admin/users' },
  { key: 'total_instructors', label: 'Total Instructors', icon: 'fas fa-chalkboard-teacher', color: 'text-indigo-600', bg: 'from-indigo-50 to-indigo-100', path: '/admin/users' },
  { key: 'total_org_admins', label: 'Total Org Admins', icon: 'fas fa-user-shield', color: 'text-purple-600', bg: 'from-purple-50 to-purple-100', path: '/admin/organizations' },
  { key: 'total_courses', label: 'Total Courses', icon: 'fas fa-book', color: 'text-emerald-600', bg: 'from-emerald-50 to-emerald-100', path: '/admin/courses' },
  { key: 'total_enrollments', label: 'Total Enrollments', icon: 'fas fa-clipboard-list', color: 'text-teal-600', bg: 'from-teal-50 to-teal-100', path: '/admin/reports/course-enrollments' },
  { key: 'total_assessments', label: 'Total Assessments', icon: 'fas fa-clipboard-check', color: 'text-amber-600', bg: 'from-amber-50 to-amber-100', path: '/admin/assessments' },
  { key: 'total_organizations', label: 'Total Organizations', icon: 'fas fa-building', color: 'text-rose-600', bg: 'from-rose-50 to-rose-100', path: '/admin/organizations' }
];

const ModuleChartCard = ({ title, subtitle, icon, color, path, onNavigate, children }) => (
  <div className="bg-white rounded-2xl shadow-xl border border-purple-100 overflow-hidden">
    <div className="flex items-center justify-between gap-3 p-5 border-b border-purple-50 bg-gradient-to-r from-white to-purple-50/30">
      <div className="flex items-center gap-3 min-w-0">
        <div className={`w-11 h-11 shrink-0 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center shadow-md`}>
          <i className={`${icon} text-white text-lg`} aria-hidden="true" />
        </div>
        <div className="min-w-0">
          <h3 className="text-base font-bold text-gray-900 truncate">{title}</h3>
          <p className="text-xs text-gray-500 truncate">{subtitle}</p>
        </div>
      </div>
      <button
        type="button"
        onClick={() => onNavigate(path)}
        className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-purple-700 bg-purple-50 rounded-full hover:bg-purple-100 transition"
        aria-label={`Open ${title} module`}
      >
        Open
        <i className="fas fa-arrow-right text-[10px]" aria-hidden="true" />
      </button>
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const Dashboard = () => {
  const { isAuthenticated, loading: authLoading, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(EMPTY_DASHBOARD);

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      fetchDashboardData();
    }
  }, [authLoading, isAuthenticated]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.dashboard.getStats();
      if (response.data?.success) {
        setDashboardData({
          ...EMPTY_DASHBOARD,
          ...response.data.data,
          counters: { ...EMPTY_DASHBOARD.counters, ...response.data.data?.counters },
          users: { ...EMPTY_DASHBOARD.users, ...response.data.data?.users },
          courses: { ...EMPTY_DASHBOARD.courses, ...response.data.data?.courses },
          organizations: { ...EMPTY_DASHBOARD.organizations, ...response.data.data?.organizations },
          assessments: { ...EMPTY_DASHBOARD.assessments, ...response.data.data?.assessments },
          vlt: { ...EMPTY_DASHBOARD.vlt, ...response.data.data?.vlt },
          licenses: { ...EMPTY_DASHBOARD.licenses, ...response.data.data?.licenses },
          vouchers: { ...EMPTY_DASHBOARD.vouchers, ...response.data.data?.vouchers },
          payments: { ...EMPTY_DASHBOARD.payments, ...response.data.data?.payments },
          forums: { ...EMPTY_DASHBOARD.forums, ...response.data.data?.forums },
          surveys: { ...EMPTY_DASHBOARD.surveys, ...response.data.data?.surveys },
          support_tickets: { ...EMPTY_DASHBOARD.support_tickets, ...response.data.data?.support_tickets }
        });
      } else {
        throw new Error(response.data?.message || 'Failed to fetch dashboard data');
      }
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const userSegmentLabels = ['Learner', 'Instructor', 'Org Learner', 'Org Instructor'];
  const userSegmentValues = useMemo(() => {
    const segment = dashboardData.users.by_segment || {};
    return [
      segment.learner || 0,
      segment.instructor || 0,
      segment.org_learner || 0,
      segment.org_instructor || 0
    ];
  }, [dashboardData.users.by_segment]);

  const courseLabels = useMemo(
    () => dashboardData.courses.all_by_enrollment.map((course) => course.course_title),
    [dashboardData.courses.all_by_enrollment]
  );

  const orgLabels = useMemo(
    () => dashboardData.organizations.by_user_type.map((org) => org.organization_name),
    [dashboardData.organizations.by_user_type]
  );

  const assessmentLabels = useMemo(
    () => dashboardData.assessments.by_assessment.map((item) => item.title),
    [dashboardData.assessments.by_assessment]
  );

  const licenseOrgLabels = useMemo(
    () => dashboardData.licenses.by_organization.map((org) => org.organization_name),
    [dashboardData.licenses.by_organization]
  );

  const forumLabels = useMemo(
    () => dashboardData.forums.by_forum.map((forum) => forum.title),
    [dashboardData.forums.by_forum]
  );

  const surveyLabels = useMemo(
    () => dashboardData.surveys.by_survey.map((survey) => survey.title),
    [dashboardData.surveys.by_survey]
  );

  const paymentTypeLabels = useMemo(
    () => dashboardData.payments.by_type.map((item) => item.payment_type),
    [dashboardData.payments.by_type]
  );

  const paymentTypeAmounts = useMemo(
    () => dashboardData.payments.by_type.map((item) => item.amount),
    [dashboardData.payments.by_type]
  );

  const paymentTrendData = useMemo(
    () => (dashboardData.payments.trend || []).map((item) => ({
      date: item.date,
      count: item.amount ?? item.count ?? 0
    })),
    [dashboardData.payments.trend]
  );

  const ticketSummary = dashboardData.support_tickets.summary || EMPTY_DASHBOARD.support_tickets.summary;

  if (authLoading || !isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent mx-auto" />
          <p className="mt-4 text-purple-800 font-semibold">Launching dashboard...</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent mx-auto" />
          <p className="mt-4 text-purple-800 font-semibold">Fetching platform analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-indigo-100 p-6">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center border border-purple-100">
          <i className="fas fa-exclamation-circle text-5xl text-red-500 mb-4" aria-hidden="true" />
          <h4 className="text-xl font-bold text-gray-900 mb-2">Unable to load dashboard</h4>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            type="button"
            onClick={fetchDashboardData}
            className="inline-flex items-center px-6 py-3 bg-purple-600 text-white font-bold rounded-full hover:bg-purple-700 transition"
          >
            <i className="fas fa-redo mr-2" aria-hidden="true" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50 p-4 sm:p-6 md:p-8">
      <div className="mb-6 bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl border border-purple-100 p-6 sm:p-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <i className="fas fa-tachometer-alt text-purple-600 text-2xl" aria-hidden="true" />
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">Super Admin Dashboard</h1>
            </div>
            <p className="text-gray-600 text-sm sm:text-base">
              Welcome back, {user?.first_name || 'Admin'}! Platform overview with module-specific analytics.
            </p>
          </div>
          <button
            type="button"
            onClick={fetchDashboardData}
            className="inline-flex items-center px-5 py-2.5 bg-white border-2 border-purple-200 text-purple-700 font-semibold rounded-full hover:bg-purple-50 transition shadow-sm"
            aria-label="Refresh dashboard data"
          >
            <i className="fas fa-sync-alt mr-2" aria-hidden="true" />
            Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-7 gap-4 mb-6">
        {KPI_COUNTERS.map((counter) => (
          <button
            key={counter.key}
            type="button"
            onClick={() => navigate(counter.path)}
            className={`bg-gradient-to-br ${counter.bg} rounded-2xl border border-white shadow-lg p-4 text-left hover:scale-105 transition-all`}
            aria-label={`View ${counter.label}`}
          >
            <div className="flex items-center justify-between mb-2">
              <i className={`${counter.icon} ${counter.color} text-lg`} aria-hidden="true" />
              <i className="fas fa-external-link-alt text-gray-400 text-xs" aria-hidden="true" />
            </div>
            <p className={`text-2xl sm:text-3xl font-bold ${counter.color}`}>
              {(dashboardData.counters[counter.key] ?? 0).toLocaleString()}
            </p>
            <p className="text-xs sm:text-sm font-medium text-gray-700 mt-1">{counter.label}</p>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <ModuleChartCard
          title="Users"
          subtitle="Learner, instructor, org learner and org instructor distribution"
          icon="fas fa-users"
          color="from-blue-500 to-blue-700"
          path="/admin/users"
          onNavigate={navigate}
        >
          <DonutChart title="Users by Role" labels={userSegmentLabels} values={userSegmentValues} />
        </ModuleChartCard>

        <ModuleChartCard
          title="Courses"
          subtitle="Individual and organization enrollments per course"
          icon="fas fa-graduation-cap"
          color="from-emerald-500 to-emerald-700"
          path="/admin/courses"
          onNavigate={navigate}
        >
          <GroupedBarChartCard
            title="Course Enrollments"
            labels={courseLabels}
            horizontal
            datasets={[
              {
                label: 'Individual',
                values: dashboardData.courses.all_by_enrollment.map((c) => c.individual_enrollments),
                color: CHART_COLORS.blue
              },
              {
                label: 'Organization',
                values: dashboardData.courses.all_by_enrollment.map((c) => c.org_enrollments),
                color: CHART_COLORS.green
              }
            ]}
          />
        </ModuleChartCard>

        <ModuleChartCard
          title="Organizations"
          subtitle="Learner and instructor count per organization"
          icon="fas fa-building"
          color="from-amber-500 to-amber-700"
          path="/admin/organizations"
          onNavigate={navigate}
        >
          <GroupedBarChartCard
            title="Organization Users"
            labels={orgLabels}
            horizontal
            datasets={[
              {
                label: 'Learners',
                values: dashboardData.organizations.by_user_type.map((org) => org.learner_count),
                color: CHART_COLORS.amber
              },
              {
                label: 'Instructors',
                values: dashboardData.organizations.by_user_type.map((org) => org.instructor_count),
                color: CHART_COLORS.purple
              }
            ]}
          />
        </ModuleChartCard>

        <ModuleChartCard
          title="Assessments"
          subtitle="Assigned learners, passed and failed per assessment"
          icon="fas fa-clipboard-check"
          color="from-indigo-500 to-indigo-700"
          path="/admin/assessments"
          onNavigate={navigate}
        >
          <GroupedBarChartCard
            title="Assessment Results"
            labels={assessmentLabels}
            horizontal
            datasets={[
              {
                label: 'Assigned',
                values: dashboardData.assessments.by_assessment.map((item) => item.assigned_count),
                color: CHART_COLORS.blue
              },
              {
                label: 'Passed',
                values: dashboardData.assessments.by_assessment.map((item) => item.passed_count),
                color: CHART_COLORS.green
              },
              {
                label: 'Failed',
                values: dashboardData.assessments.by_assessment.map((item) => item.failed_count),
                color: CHART_COLORS.rose
              }
            ]}
          />
        </ModuleChartCard>

        <ModuleChartCard
          title="Virtual-Led Training"
          subtitle="Video, document and PDF content breakdown"
          icon="fas fa-laptop-code"
          color="from-orange-500 to-orange-700"
          path="/admin/vlt"
          onNavigate={navigate}
        >
          <BarChartCard
            title="VLT Content Types"
            labels={['Video', 'Document', 'PDF']}
            values={[
              dashboardData.vlt.by_type?.video || 0,
              dashboardData.vlt.by_type?.document || 0,
              dashboardData.vlt.by_type?.pdf || 0
            ]}
            color={CHART_COLORS.amber}
          />
        </ModuleChartCard>

        <ModuleChartCard
          title="Licenses"
          subtitle="Requests, assigned, users created and remaining per organization"
          icon="fas fa-key"
          color="from-yellow-500 to-yellow-700"
          path="/admin/licenses"
          onNavigate={navigate}
        >
          <GroupedBarChartCard
            title="License Utilization by Organization"
            labels={licenseOrgLabels}
            horizontal
            datasets={[
              {
                label: 'Requests',
                values: dashboardData.licenses.by_organization.map((org) => org.requests),
                color: CHART_COLORS.amber
              },
              {
                label: 'Assigned',
                values: dashboardData.licenses.by_organization.map((org) => org.assigned),
                color: CHART_COLORS.blue
              },
              {
                label: 'Users Created',
                values: dashboardData.licenses.by_organization.map((org) => org.users_created),
                color: CHART_COLORS.green
              },
              {
                label: 'Remaining',
                values: dashboardData.licenses.by_organization.map((org) => org.remaining),
                color: CHART_COLORS.purple
              }
            ]}
          />
        </ModuleChartCard>

        <ModuleChartCard
          title="Vouchers"
          subtitle="Vouchers created versus used"
          icon="fas fa-tags"
          color="from-teal-500 to-teal-700"
          path="/admin/vouchers"
          onNavigate={navigate}
        >
          <BarChartCard
            title="Voucher Usage"
            labels={['Created', 'Used']}
            values={[
              dashboardData.vouchers.chart?.created || 0,
              dashboardData.vouchers.chart?.used || 0
            ]}
            color={CHART_COLORS.cyan}
          />
        </ModuleChartCard>

        <ModuleChartCard
          title="Transactions"
          subtitle="Payment amount received by type and trend"
          icon="fas fa-credit-card"
          color="from-green-500 to-green-700"
          path="/admin/reports/users"
          onNavigate={navigate}
        >
          <div className="space-y-4">
            <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-4 text-center">
              <p className="text-xs text-gray-600 mb-1">Total Amount Received</p>
              <p className="text-2xl font-bold text-emerald-700">
                ${Number(dashboardData.payments.total_received || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
            <BarChartCard
              title="Amount by Payment Type"
              labels={paymentTypeLabels}
              values={paymentTypeAmounts}
              color={CHART_COLORS.green}
            />
            <LineTrendChart
              data={paymentTrendData}
              label="Daily Revenue"
              color={CHART_COLORS.green}
              yAxisLabel="Amount ($)"
            />
          </div>
        </ModuleChartCard>

        <ModuleChartCard
          title="Forums"
          subtitle="Views, likes and comments per forum"
          icon="fas fa-comments"
          color="from-cyan-500 to-cyan-700"
          path="/admin/forums"
          onNavigate={navigate}
        >
          <GroupedBarChartCard
            title="Forum Engagement"
            labels={forumLabels}
            horizontal
            datasets={[
              {
                label: 'Views',
                values: dashboardData.forums.by_forum.map((forum) => forum.views),
                color: CHART_COLORS.blue
              },
              {
                label: 'Likes',
                values: dashboardData.forums.by_forum.map((forum) => forum.likes),
                color: CHART_COLORS.rose
              },
              {
                label: 'Comments',
                values: dashboardData.forums.by_forum.map((forum) => forum.comments),
                color: CHART_COLORS.cyan
              }
            ]}
          />
        </ModuleChartCard>

        <ModuleChartCard
          title="Surveys"
          subtitle="Surveys created and responses received"
          icon="fas fa-poll"
          color="from-fuchsia-500 to-fuchsia-700"
          path="/admin/surveys"
          onNavigate={navigate}
        >
          <GroupedBarChartCard
            title="Survey Responses"
            labels={surveyLabels}
            horizontal
            datasets={[
              {
                label: 'Created',
                values: dashboardData.surveys.by_survey.map(() => 1),
                color: CHART_COLORS.purple
              },
              {
                label: 'Responses',
                values: dashboardData.surveys.by_survey.map((survey) => survey.responses),
                color: CHART_COLORS.pink
              }
            ]}
          />
        </ModuleChartCard>

        <ModuleChartCard
          title="Support Tickets"
          subtitle="Received, pending, resolved and completed tickets"
          icon="fas fa-headset"
          color="from-rose-500 to-rose-700"
          path="/admin/support-tickets"
          onNavigate={navigate}
        >
          <BarChartCard
            title="Ticket Status Summary"
            labels={['Received', 'Pending', 'Resolved', 'Completed']}
            values={[
              ticketSummary.received,
              ticketSummary.pending,
              ticketSummary.resolved,
              ticketSummary.completed
            ]}
            color={CHART_COLORS.rose}
          />
        </ModuleChartCard>
      </div>
    </div>
  );
};

export default Dashboard;
